import { noteImageFailure } from './imageLoadQueue'
import { nhost } from '@/shared/lib/nhost'

export function isMobile(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

interface ImageOptions {
  width?: number
  height?: number
  quality?: number
  format?: 'webp' | 'jpg' | 'png' | 'avif'
}

/**
 * Cache-busting token appended to every image URL. Storage files are sometimes
 * replaced in place (same file id, new bytes — e.g. scripts/shrink-storage-images.mjs),
 * and browsers/CDN cache image responses by URL (max-age 1h, CDN edge up to 7d). Bumping
 * this value changes every image URL, so all clients fetch the new bytes immediately
 * instead of serving stale cached copies. Bump it whenever storage files are replaced.
 */
export const IMAGE_CACHE_VERSION = '2'

export function getImageUrl(fileId: string, options?: ImageOptions): string {
  const baseUrl = nhost.storage.getPublicUrl({ fileId })

  const params = new URLSearchParams()
  if (options?.width) params.set('w', options.width.toString())
  if (options?.height) params.set('h', options.height.toString())
  if (options?.quality) params.set('q', options.quality.toString())
  if (options?.format) params.set('f', options.format)
  params.set('v', IMAGE_CACHE_VERSION)

  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}${params.toString()}`
}

export function getThumbnailUrl(fileId: string): string {
  const mobile = isMobile()
  return getImageUrl(
    fileId,
    mobile
      ? { width: 400, quality: 75, format: 'webp' }
      : { width: 600, quality: 85, format: 'webp' }
  )
}

export function getChapterImageUrl(fileId: string): string {
  const mobile = isMobile()
  return getImageUrl(
    fileId,
    mobile
      ? { width: 480, quality: 75, format: 'webp' }
      : { width: 800, quality: 85, format: 'webp' }
  )
}

export function getAvatarUrl(fileId: string): string {
  return getImageUrl(fileId, { width: 200, quality: 80, format: 'webp' })
}

/** How many times to retry a failed transform before falling back to the original. */
export const MAX_TRANSFORM_RETRIES = 3

/**
 * onError handler for plain <img> tags rendering transformed (resized/webp) images.
 *
 * The Nhost storage endpoint is rate limited per project: when a page requests many
 * not-yet-CDN-cached images at once, the ingress answers 502/503 for essentially the
 * whole burst. The same URLs load fine once requested slowly, so a failure here means
 * "back off", not "this image is broken".
 *
 * Retrying must therefore wait out the *global* cooldown in `imageLoadQueue` — retrying
 * after a few hundred ms lands inside the still-active block, extends it, and burns the
 * budget of every sibling image. Only once the retries are exhausted do we fall back to
 * the original, untransformed file, for source formats the transform genuinely can't
 * handle (animated GIFs, SVGs).
 */
export function fallbackToOriginalOnError(e: { currentTarget: HTMLImageElement }): void {
  const img = e.currentTarget
  if (!img.src || !img.src.includes('?')) return // original already, nothing to warm up

  const wait = noteImageFailure()
  // Ein kaputter Zähler (kein Wert, leer, Text) zählt als Neustart - sonst wäre er NaN,
  // NaN >= MAX nie wahr, und das Bild käme nie beim Original an.
  const parsedAttempts = Number(img.dataset.transformRetry ?? '0')
  const attempts = Number.isFinite(parsedAttempts) ? parsedAttempts : 0
  if (attempts >= MAX_TRANSFORM_RETRIES) {
    img.dataset.transformRetry = ''
    img.src = img.src.split('?')[0]
    return
  }

  img.dataset.transformRetry = String(attempts + 1)
  const url = img.src
  window.setTimeout(() => {
    // Re-fetch the *same* URL (by now the rate limit window has passed and the CDN may
    // even have it). Removing the attribute first forces the browser to re-issue it.
    img.removeAttribute('src')
    img.src = url
  }, wait)
}
